import { z } from "zod";
import { localizedTextSchema, type LocalizedText } from "./localized-text";
import { SITE_ICON_NAMES } from "./site-icon-names";
import { siteTargetSchema, type SiteTarget } from "./site-target";
import { verifyExternalAddress, verifyMenuShape, verifyRoutePath } from "./verify-site-layout";

/** The widths a footer link column can have, next to the others on a wide screen. */
export const FOOTER_WIDTHS = ["narrow", "normal", "wide"] as const;

/** How the drop-down panels of the top menu are laid out: at most this many columns side by side (fewer on a narrow screen), and whether each link shows its icon and its description. */
export const PANEL_COLUMNS = [1, 2, 3, 4] as const;
export const panelSchema = z.object({ columns: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]), icons: z.boolean(), descriptions: z.boolean(), wide: z.boolean().optional(), full: z.boolean().optional() });
export type MenuPanel = z.infer<typeof panelSchema>;
export const DEFAULT_PANEL: MenuPanel = { columns: 3, icons: true, descriptions: true };

/** An optional picture for a menu link: a path of this site or an https address. */
export const menuImageSchema = z.string().max(500).refine((v) => (v.startsWith("/") ? verifyRoutePath(v).ok : verifyExternalAddress(v).ok));

/** The big card at the end of a drop-down panel: a picture with a title, a line of text and a button. */
export type MenuFeature = { image: string; title: LocalizedText; text: LocalizedText; label: LocalizedText; target: SiteTarget };
const menuFeatureSchema = z.object({ image: menuImageSchema, title: localizedTextSchema, text: localizedTextSchema, label: localizedTextSchema, target: siteTargetSchema });

/** One entry of the menu or of a footer column: a link, or (with `children`) a group / submenu of entries. `app` ties it to an optional app: it disappears while that app is off. */
export type MenuNode = {
  id: string;
  label: LocalizedText;
  description: LocalizedText;
  icon: (typeof SITE_ICON_NAMES)[number];
  target: SiteTarget;
  app?: string;
  hidden: boolean;
  /** An offer announced but not sold yet: shown with a "Soon" tag and linking nowhere. */
  soon?: boolean;
  /** No icon at all in the drop-down panel (a picture still shows if there is one). */
  noIcon?: boolean;
  /** A picture shown beside the link in a drop-down panel (instead of its icon). */
  image?: string;
  /** Footer link columns only: how wide the column is next to the others. */
  width?: (typeof FOOTER_WIDTHS)[number];
  /** Top-level groups of the menu only: the featured card at the end of the panel. */
  feature?: MenuFeature;
  /** Top-level groups of the menu only: a wide card under the columns of the panel, with a picture, text and a button. */
  promo?: MenuFeature;
  /** A group shown folded in the drop-down panel: closed at first, opened with a button. */
  fold?: boolean;
  source?: boolean;
  children?: MenuNode[];
};

export const menuNodeSchema: z.ZodType<MenuNode> = z.lazy(() => z.object({
  id: z.string().regex(/^[a-z0-9-]{1,40}$/),
  label: localizedTextSchema,
  description: localizedTextSchema,
  icon: z.enum(SITE_ICON_NAMES),
  target: siteTargetSchema,
  app: z.string().regex(/^[a-z0-9-]{1,40}$/).optional(),
  hidden: z.boolean(),
  soon: z.boolean().optional(),
  noIcon: z.boolean().optional(),
  image: menuImageSchema.optional(),
  width: z.enum(FOOTER_WIDTHS).optional(),
  feature: menuFeatureSchema.optional(),
  promo: menuFeatureSchema.optional(),
  fold: z.boolean().optional(),
  source: z.boolean().optional(),
  children: z.array(menuNodeSchema).max(20).optional(),
}));

/** The top navigation of the public site: entries (single links or groups with submenus) and the button at the end (the account button as today, one of the owner's own, or none). */
export const siteMenuSchema = z.object({
  nodes: z.array(menuNodeSchema).max(12),
  panel: panelSchema.optional(),
  cta: z.object({ mode: z.enum(["account", "custom", "none"]), label: localizedTextSchema, target: siteTargetSchema }),
}).refine((menu) => verifyMenuShape(menu.nodes).ok);

export type SiteMenu = z.infer<typeof siteMenuSchema>;

/** A link entry (no children), shown, going nowhere until `extra` gives it a target. */
export function menuLink(id: string, icon: MenuNode["icon"], label: LocalizedText, description: LocalizedText = {}, extra: Partial<MenuNode> = {}): MenuNode {
  return { id, label, description, icon, target: { type: "none" }, hidden: false, ...extra };
}

/** A group entry: a title and its children (links or submenus). */
export function menuGroup(id: string, label: LocalizedText, children: MenuNode[]): MenuNode {
  return { ...menuLink(id, "link", label), children };
}

/** A copy of an entry and everything under it, under new ids; the copy is not the "What we do" source, so only one entry holds that. */
export function duplicateMenuNode(node: MenuNode): MenuNode {
  const rest = { ...node };
  delete rest.source;
  return { ...rest, id: `n-${crypto.randomUUID().slice(0, 8)}`, ...(node.children ? { children: node.children.map(duplicateMenuNode) } : {}) };
}

/** A new, empty entry for the editor: a link, or a group (with no entries yet), under an id of its own. */
export function newMenuNode(kind: "link" | "group"): MenuNode {
  const node = menuLink(`n-${crypto.randomUUID().slice(0, 8)}`, "link", {});
  return kind === "group" ? { ...node, children: [] } : node;
}
